import { useAddOrModifyKlus } from "@personeel/hooks/useFetchData.ts";
import { KlusModel } from "@personeel/models/KlusModel";
import { PersoneelsdienstModel } from "@personeel/models/PersoneelsdienstModel";
import { usePersoneelsinstellingenStore } from "@personeel/stores/PersoneelsinstellingenStore.ts";
import { useKlussenStore } from "@personeel/stores/KlussenStore.ts";
import { usePersoneelsdienstenStore } from "@personeel/stores/PersoneelsdienstenStore.ts";
import { useRef, useState } from "react";

/** Maximale scrollsnelheid in pixels per animatieframe */
const MAX_SCROLL_SPEED = 10;

export interface UseKlusDragTussenDienstenOptions {
  klus: KlusModel;
}

export interface UseKlusDragTussenDienstenResult {
  isDragging: boolean;
  startDrag: (startClientX: number, startClientY: number) => void;
  onDragMove: (clientX: number, clientY: number) => void;
  onDragEnd: (clientY: number) => void;
  cancelDrag: () => void;
}

/**
 * Hook voor het verticaal (tussen diensten) verschuiven van een klus.
 *
 * Kernstrategie:
 * - RAF-loop (requestAnimationFrame) beheert alle visuele updates en auto-scroll.
 *   tickFrame wordt elke frame aangeroepen; stopRaf() stopt de loop.
 * - Bounds (eerste/laatste dienstrij) worden opgeslagen in document-Y = clientY + scrollTop.
 *   Dit is stabiel: verandert niet als de container scrollt.
 * - visibleTop = onderkant sticky topbar (live getBoundingClientRect().bottom elke frame,
 *   want sticky elementen geven altijd hun viewport-positie terug).
 * - visibleBottom = onderkant container (opgeslagen bij startDrag, verandert niet).
 * - Scroll-trigger: klusbovenkant < visibleTop → scroll omhoog;
 *                   klusonderkant > visibleBottom → scroll omlaag.
 * - Dropzone: c-duty-drop-zone divs met data-werkperiode-id,
 *   gepositioneerd per werkperiodeInDienst in Personeelsdienst.tsx.
 */
export function useKlusDragTussenDiensten({
  klus,
}: UseKlusDragTussenDienstenOptions): UseKlusDragTussenDienstenResult {
  const { mutate: addOrModifyKlus } = useAddOrModifyKlus();
  const personeelsdiensten = usePersoneelsdienstenStore((s) => s.personeelsdiensten);
  const { personeelsinstellingen } = usePersoneelsinstellingenStore();
  const setKlussen = useKlussenStore((s) => s.setKlussen);

  const [isDragging, setIsDragging] = useState(false);

  const dragElementRef = useRef<HTMLElement | null>(null);
  // DOM-element van de sticky topbar (gevonden bij startDrag via data-topbar)
  const topbarElementRef = useRef<Element | null>(null);
  // Hoogte van de klus in pixels
  const klusHeightRef = useRef<number>(0);
  // Cursor-offset t.o.v. bovenkant klus bij dragstart
  const cursorOffsetInKlusRef = useRef<number>(0);

  // Document-Y grenzen: stabiel, veranderen niet bij scrollen.
  // documentY = clientY + container.scrollTop
  const minKlusDocY = useRef<number>(0); // bovenkant eerste dienstrij
  const maxKlusDocY = useRef<number>(0); // onderkant laatste dienstrij

  // Onderkant container in viewport (opgeslagen bij startDrag, verandert niet)
  const containerBottomRef = useRef<number>(0);

  // Originele klusbovenkant (voor het draggen) in document-Y (stabiel)
  const origKlusDocY = useRef<number>(0);

  const clientXRef = useRef<number>(0);
  const rawClientY = useRef<number>(0);
  // Geclamped klusbovenkant clientY (bijgewerkt door RAF-loop, gebruikt bij drop)
  const lastKlusBovenkantClientY = useRef<number>(0);

  // Huidig gemarkeerde active-line element (voor highlight tijdens drag)
  const highlightedActiveLineRef = useRef<HTMLElement | null>(null);

  // requestAnimationFrame handle
  const rafRef = useRef<number | null>(null);

  /** Verwijder de valid-drop highlight van het huidige element */
  function clearHighlight() {
    if (highlightedActiveLineRef.current) {
      highlightedActiveLineRef.current.classList.remove("c-duty-active-line--valid-drop");
      highlightedActiveLineRef.current = null;
    }
  }

  /**
   * Zet de valid-drop highlight op de c-duty-active-line die hoort bij werkperiodeId.
   * Verwijdert eerst de highlight van het vorige element.
   */
  function setHighlight(werkperiodeId: string | null) {
    clearHighlight();
    if (!werkperiodeId) return;
    const activeLine = document.querySelector<HTMLElement>(
      `[data-active-line-id="${werkperiodeId}"]`,
    );
    if (activeLine) {
      activeLine.classList.add("c-duty-active-line--valid-drop");
      highlightedActiveLineRef.current = activeLine;
    }
  }

  /**
   * Past de translateY-transform toe op het gesleepte klus-element zodat het visueel
   * op de juiste verticale positie staat.
   * translateY = klusBovenkantClientY - origClientY
   * waarbij origClientY de oorspronkelijke viewport-positie van de klusbovenkant is,
   * omgerekend met de huidige scrollTop zodat auto-scroll correct meegenomen wordt.
   */
  function updateVisuele(klusBovenkantClientY: number, scrollTop: number) {
    if (!dragElementRef.current) return;
    const origClientY = origKlusDocY.current - scrollTop;
    dragElementRef.current.style.transform = `translateY(${klusBovenkantClientY - origClientY}px)`;
  }

  /** Stop de RAF-loop */
  function stopRaf() {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }

  function vindDropZone(clientX: number, clientY: number): HTMLElement | null {
    const dropZones = Array.from(
      document.querySelectorAll<HTMLElement>("[data-werkperiode-id], [data-unassigned-drop]"),
    );
    return (
      dropZones.find((el) => {
        const rect = el.getBoundingClientRect();
        return (
          clientY >= rect.top &&
          clientY <= rect.bottom &&
          clientX >= rect.left &&
          clientX <= rect.right
        );
      }) ?? null
    );
  }

  /**
   * RAF-loop: elke frame:
   * 1. Lees raw muispositie en huidige scrollTop
   * 2. Bereken geclamped klusbovenkant (documentY → clamp → clientY)
   * 3. Scroll als klusrand de viewport-rand raakt (trigger op dienstrij-grens)
   * 4. Update visuele positie (translateY) en highlight
   */
  function tickFrame() {
    const container = document.getElementById("right-div");
    if (!container || !dragElementRef.current) return;

    const scrollTop = container.scrollTop;
    const rawY = rawClientY.current;

    // visibleTop: onderkant sticky topbar (live, want sticky = altijd viewport-positie)
    const visibleTop = topbarElementRef.current
      ? topbarElementRef.current.getBoundingClientRect().bottom
      : containerBottomRef.current;
    const visibleBottom = containerBottomRef.current;

    // Geclamped klusbovenkant: raw documentY → clamp op dienstrij-bounds → clientY
    const rawDocY = rawY - cursorOffsetInKlusRef.current + scrollTop;
    const clampedDocY = Math.max(
      minKlusDocY.current,
      Math.min(maxKlusDocY.current - klusHeightRef.current, rawDocY),
    );
    const klusBovenkant = clampedDocY - scrollTop;
    const klusOnderkant = klusBovenkant + klusHeightRef.current;

    const firstRowClientTop = minKlusDocY.current - scrollTop;
    const lastRowClientBottom = maxKlusDocY.current - scrollTop;

    let scrollDelta = 0;
    if (klusBovenkant < visibleTop && firstRowClientTop < visibleTop) {
      scrollDelta = -Math.min(MAX_SCROLL_SPEED, visibleTop - firstRowClientTop);
    } else if (klusOnderkant > visibleBottom && lastRowClientBottom > visibleBottom) {
      scrollDelta = Math.min(MAX_SCROLL_SPEED, lastRowClientBottom - visibleBottom);
    }

    if (scrollDelta !== 0) {
      container.scrollTop = scrollTop + scrollDelta;
    }

    // Herbereken na eventuele scroll
    const finalScrollTop = container.scrollTop;
    const finalKlusBovenkant =
      Math.max(
        minKlusDocY.current,
        Math.min(
          maxKlusDocY.current - klusHeightRef.current,
          rawY - cursorOffsetInKlusRef.current + finalScrollTop,
        ),
      ) - finalScrollTop;

    lastKlusBovenkantClientY.current = finalKlusBovenkant;
    updateVisuele(finalKlusBovenkant, finalScrollTop);

    // Highlight: bepaal welke dropzone onder de cursor ligt
    dragElementRef.current.style.visibility = "hidden";
    const dropClientY = finalKlusBovenkant + cursorOffsetInKlusRef.current;
    const elementOnder = vindDropZone(clientXRef.current, dropClientY);
    const werkperiodeId =
      elementOnder?.dataset["werkperiodeId"] ??
      elementOnder?.closest<HTMLElement>("[data-werkperiode-id]")?.dataset["werkperiodeId"] ??
      null;

    dragElementRef.current.style.visibility = "";
    setHighlight(werkperiodeId ?? null);

    rafRef.current = requestAnimationFrame(tickFrame);
  }

  /**
   * Bepaal de werkperiode waarop gedropt wordt via data-werkperiode-id
   * van de c-duty-drop-zone div.
   * Valideert dat de klus in zijn geheel past binnen [starttijd + voortijd, eindtijd - natijd].
   */
  function vindWerkperiodeOpDroppunt(elementOnder: Element): PersoneelsdienstModel | null {
    const werkperiodeId =
      (elementOnder as HTMLElement).dataset["werkperiodeId"] ??
      elementOnder.closest<HTMLElement>("[data-werkperiode-id]")?.dataset["werkperiodeId"];
    if (!werkperiodeId) return null;

    const werkperiode = personeelsdiensten
      .flatMap((d) => d.personeelsdiensten)
      .find((wp) => wp.id === werkperiodeId);
    if (!werkperiode) return null;

    // Valideer dat de klus in zijn geheel past binnen geldig dropvenster
    const dropVensterBegin = werkperiode.starttijd.plus({
      minutes: personeelsinstellingen.voortijd,
    });
    const dropVensterEind = werkperiode.eindtijd.minus({ minutes: personeelsinstellingen.natijd });

    if (klus.beginDatumtijd < dropVensterBegin || klus.eindDatumtijd > dropVensterEind) {
      return null;
    }

    return werkperiode;
  }

  /**
   * Als niet gedropt kan worden gaat klus terug naar oorspronkelijke positie
   */
  function resetVisuelePositie() {
    clearHighlight();
    if (dragElementRef.current) {
      dragElementRef.current.style.transform = "";
      dragElementRef.current.style.zIndex = "";
    }
  }

  function startDrag(startClientX: number, startClientY: number) {
    // scrollbare container-element die de tijdlijn en alle personeelsdiensten bevat en verticaal scrollbaar is.
    const container = document.getElementById("right-div");
    const scrollTop = container?.scrollTop ?? 0;

    clientXRef.current = startClientX;
    rawClientY.current = startClientY;

    dragElementRef.current = document.getElementById("ScrollableTypes.Klus" + klus.id);

    if (dragElementRef.current) {
      const klusRect = dragElementRef.current.getBoundingClientRect();
      klusHeightRef.current = klusRect.height;
      cursorOffsetInKlusRef.current = startClientY - klusRect.top;
      origKlusDocY.current = klusRect.top + scrollTop;
      dragElementRef.current.style.zIndex = "20";
    } else {
      klusHeightRef.current = 0;
      cursorOffsetInKlusRef.current = 0;
      origKlusDocY.current = startClientY + scrollTop;
    }

    if (container) {
      containerBottomRef.current = container.getBoundingClientRect().bottom;
      topbarElementRef.current = container.querySelector("[data-topbar]");

      // Bounds: document-Y van bovenkant eerste en onderkant laatste dienstrij
      const rows = Array.from(container.querySelectorAll<HTMLElement>("[data-duty-group-id]"));
      if (rows.length > 0) {
        minKlusDocY.current = rows[0].getBoundingClientRect().top + scrollTop;
        maxKlusDocY.current = rows.at(-1)!.getBoundingClientRect().bottom + scrollTop;
      } else {
        minKlusDocY.current = 0;
        maxKlusDocY.current = 9_999_999;
      }
    }

    lastKlusBovenkantClientY.current = origKlusDocY.current - scrollTop;

    setHighlight(klus.dienst ?? null);
    setIsDragging(true);
    rafRef.current = requestAnimationFrame(tickFrame);
  }

  function onDragMove(clientX: number, clientY: number) {
    clientXRef.current = clientX;
    rawClientY.current = clientY;
  }

  function rollbackKlus() {
    setKlussen((prev) => prev.map((k) => (k.id === klus.id ? klus : k)));
  }

  function onDragEnd(clientY: number) {
    if (dragElementRef.current === null) return;

    stopRaf();
    rawClientY.current = clientY;

    // Verberg klus zodat elementFromPoint het element eronder vindt
    dragElementRef.current.style.visibility = "hidden";
    const dropClientY = lastKlusBovenkantClientY.current + cursorOffsetInKlusRef.current;
    const elementOnder = vindDropZone(clientXRef.current, dropClientY);
    dragElementRef.current.style.visibility = "";

    clearHighlight();

    let gedropt = false;
    if (elementOnder) {
      if (elementOnder.closest("[data-unassigned-drop]")) {
        if (klus.dienst) {
          const gewijzigdeKlus = { ...klus, dienst: undefined };
          setKlussen((prev) => prev.map((k) => (k.id === klus.id ? gewijzigdeKlus : k)));
          addOrModifyKlus(gewijzigdeKlus, {
            onError: rollbackKlus,
          });
          gedropt = true;
        }
      }

      const werkperiode = vindWerkperiodeOpDroppunt(elementOnder);
      if (!gedropt && werkperiode && werkperiode.id !== klus.dienst) {
        const gewijzigdeKlus = { ...klus, dienst: werkperiode.id };
        // Optimistic update: store direct bijwerken zodat React meteen re-rendert
        // en de klus op de nieuwe dienstrij verschijnt zonder zichtbare vertraging.
        // Bij server-fout wordt de originele klus teruggezet via onError.
        setKlussen((prev) => prev.map((k) => (k.id === klus.id ? gewijzigdeKlus : k)));
        addOrModifyKlus(gewijzigdeKlus, {
          onError: rollbackKlus,
        });
        gedropt = true;
      }
    }

    if (!gedropt) resetVisuelePositie();

    dragElementRef.current = null;
    topbarElementRef.current = null;
    setIsDragging(false);
  }

  function cancelDrag() {
    stopRaf();
    resetVisuelePositie();
    dragElementRef.current = null;
    topbarElementRef.current = null;
    setIsDragging(false);
  }

  return { isDragging, startDrag, onDragMove, onDragEnd, cancelDrag };
}
